import { type ChartArea, type Chart, type FontSpec } from "chart.js";

export const createDiagonalPattern = ({ distance = 1, color = "#7081FF" }) => {
  const shape = document.createElement("canvas");
  shape.width = 5 * distance;
  shape.height = 5 * distance;
  const ctx = shape.getContext("2d");
  if (ctx) {
    ctx.strokeStyle = color;
    ctx.beginPath();
    ctx.moveTo(0, 1 * distance);
    ctx.lineTo(1 * distance, 0);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(1 * distance, 5 * distance);
    ctx.lineTo(5 * distance, 1 * distance);
    ctx.stroke();
    return ctx.createPattern(shape, "repeat") ?? color;
  }
  return color;
};

export const drawLine = (
  ctx: CanvasRenderingContext2D,
  moveTo: { x: number; y: number },
  lineTo: { x: number; y: number },
) => {
  ctx.beginPath();
  ctx.moveTo(moveTo.x, moveTo.y);
  ctx.lineTo(lineTo.x, lineTo.y);
  ctx.stroke();
};

export const drawMetricTitle = (
  ctx: CanvasRenderingContext2D,
  metricName = "",
  {
    top,
    titleXPosition,
    font,
  }: { top: number; titleXPosition: number; font: FontSpec },
) => {
  ctx.save();
  ctx.font = `${font.weight} ${font.size}px ${font.family}`;
  ctx.fillText(metricName, titleXPosition, top - 24);
  ctx.restore();
};

export const drawAlternativeMetricTitle = (
  ctx: CanvasRenderingContext2D,
  metricName = "",
  {
    x,
    font,
    tickWidth,
    chartArea: { top, bottom },
    chart,
    config,
  }: {
    font: FontSpec;
    x: number;
    tickWidth: number;
    chartArea: ChartArea;
    chart: Chart;
    config?: any;
  },
) => {
  if (!config) {
    return;
  }
  const chartType = config?.type ?? chart.config.type;

  ctx.save();
  ctx.font = `${font.weight} ${font.size}px ${font.family}`;
  ctx.strokeStyle = config.color ?? "#7081FF";
  ctx.lineWidth = 2;
  if (chartType === "bar") {
    ctx.setLineDash([]);
  } else {
    ctx.setLineDash(config.borderDash || []);
  }
  drawLine(ctx, { x, y: top - 1 }, { x, y: bottom + 1 });
  drawLine(ctx, { x, y: top }, { x: x + tickWidth, y: top });
  drawLine(ctx, { x, y: bottom }, { x: x + tickWidth, y: bottom });

  const measuredText = ctx.measureText(metricName);
  const textWidth = measuredText.width;
  const textHeight =
    measuredText.actualBoundingBoxAscent +
    measuredText.actualBoundingBoxDescent;

  const XOffset = x + textHeight / 2;
  const YOffset = (top + bottom) / 2 + textWidth / 2;

  ctx.fillStyle = config.scaleBackgroundColor ?? "white";

  ctx.fillRect(
    x - textHeight / 2,
    (top + bottom) / 2 - textWidth / 2 - 5,
    textHeight,
    textWidth + 10,
  );

  ctx.fillStyle = config.color ?? "#7081FF";
  ctx.translate(XOffset, YOffset);
  ctx.rotate(-Math.PI / 2);
  ctx.fillText(metricName, 0, 0);

  if (chartType === "bar") {
    const borderWidth = 0.1;
    const titleLegendX = -16;
    const titleLegendY = -8;
    const legendHeight = 8;
    const legendWidth = 12;

    // white bg for the legend
    ctx.beginPath();
    ctx.rect(titleLegendX - 6, titleLegendY, legendWidth + 10, legendHeight);
    ctx.fillStyle = config.scaleBackgroundColor ?? "white";
    ctx.fill();

    ctx.beginPath();
    ctx.lineWidth = 1;

    ctx.roundRect(
      titleLegendX - borderWidth / 2,
      titleLegendY - borderWidth / 2,
      legendWidth + borderWidth,
      legendHeight + borderWidth,
    );
    ctx.stroke();
    ctx.fillStyle = config.baseBackgroundColor;
    ctx.fillRect(titleLegendX, titleLegendY, legendWidth, legendHeight);
  }
  ctx.restore();
};

export const drawIcon = ({
  chart: { ctx },
  x,
  y,
  height,
  width,
  icon,
}: {
  chart: Chart;
  x: number;
  y: number;
  height: number;
  width: number;
  icon: string;
}) => {
  ctx.save();
  const img = new Image();
  img.src = `data:image/svg+xml;base64,${btoa(icon)}`;
  ctx.drawImage(img, x, y, width, height);
  ctx.restore();
};
